import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  AlertCircle, MapPin, Calendar, CheckCircle2, Star, Wallet, ShieldAlert,
  Zap, ClipboardList, ArrowRight, ShieldCheck,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const STATUS_STYLES = {
  matched:      "bg-blue-50 text-blue-700 border-blue-200",
  active:       "bg-teal-50 text-teal-700 border-teal-200",
  in_progress:  "bg-teal-50 text-teal-700 border-teal-200",
  completed:    "bg-slate-100 text-slate-700 border-slate-200",
};

export default function CleanerPortal() {
  const { user } = useApp();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [mine, setMine] = useState([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [earnings, setEarnings] = useState(null);
  const [eligibility, setEligibility] = useState(null);

  const load = async () => {
    try {
      const p = await api.get("/cleaner/profile"); setProfile(p.data);
      const m = await api.get("/jobs/mine"); setMine(m.data);
      if (p.data?.verification_status === "approved") {
        try { const av = await api.get("/jobs/available"); setAvailableCount((av.data || []).length); } catch (_) {}
        try { const e  = await api.get("/cleaner/earnings"); setEarnings(e.data); } catch (_) {}
        try { const el = await api.get("/cleaner/eligibility"); setEligibility(el.data); } catch (_) {}
      }
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  // Cleaner Agreement HARD-BLOCK (unchanged from previous portal — still required at portal entry).
  const [agreementDoc, setAgreementDoc] = useState(null);
  const [agreementAccepting, setAgreementAccepting] = useState(false);
  const requireAgreement = !!(
    profile?.verification_status === "approved" &&
    eligibility && eligibility.agreement_ok === false
  );
  useEffect(() => {
    if (!requireAgreement || agreementDoc) return;
    api.get("/cleaner/agreement/latest").then(({ data }) => setAgreementDoc(data)).catch(() => {});
  }, [requireAgreement, agreementDoc]);
  const acceptCurrentAgreement = async () => {
    if (!agreementDoc?.version) return;
    setAgreementAccepting(true);
    try {
      await api.post("/cleaner/agreement/accept", { version: agreementDoc.version, accepted: true });
      toast.success("Agreement accepted — you are now eligible for jobs");
      const el = await api.get("/cleaner/eligibility"); setEligibility(el.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record acceptance");
    } finally { setAgreementAccepting(false); }
  };

  if (!profile) return <div className="max-w-3xl mx-auto p-12 text-slate-500" data-testid="portal-loading">Loading…</div>;

  if (!profile.legal_first_name) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center">
        <AlertCircle className="w-14 h-14 text-amber-500 mx-auto" />
        <h2 className="font-display font-bold text-2xl mt-4">Complete onboarding first</h2>
        <p className="text-slate-600 mt-2">Fill out your profile to start receiving jobs.</p>
        <Button className="mt-6 bg-teal-600 text-white" onClick={() => nav("/cleaner/onboarding")} data-testid="cleaner-goto-onboarding">Start onboarding</Button>
      </div>
    );
  }

  const active = mine.filter(j => ["matched", "active", "in_progress"].includes(j.status));
  const nextActive = [...active].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-teal-600">Cleaner Portal</div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Hi, {user?.full_name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">Your quick overview. Use the cards below to open Available Jobs, Schedule, Earnings, or the Safety Checklist.</p>
        </div>
        <Badge className={
          profile.verification_status === "approved" ? "bg-teal-100 text-teal-800 border-teal-200" :
          profile.verification_status === "pending_id_review" ? "bg-amber-100 text-amber-800 border-amber-200" :
          "bg-slate-100 text-slate-700"
        } data-testid="portal-verification-badge">
          {profile.verification_status === "approved" ? <><CheckCircle2 className="w-3 h-3 mr-1" />Verified</> : profile.verification_status.replace(/_/g, " ")}
        </Badge>
      </div>

      {profile.verification_status !== "approved" && (
        <Card className="mb-6 border-amber-200 bg-amber-50" data-testid="portal-awaiting-verification">
          <CardContent className="p-5 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div>
              <div className="font-semibold text-amber-900">Awaiting verification</div>
              <div className="text-sm text-amber-700">Once approved by admin, you'll see available jobs in your zone.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {profile.verification_status === "approved" && eligibility && !eligibility.eligible_for_transport_jobs && (
        <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="risk-banner">
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-amber-600 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">Finish CLEANBAZ safety onboarding to accept jobs</div>
                <div className="text-sm text-amber-800 mt-0.5">Open your Safety Checklist to see what's outstanding.</div>
              </div>
            </div>
            <Button onClick={() => nav("/cleaner/safety-checklist")} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="risk-banner-cta">
              Open Safety Checklist
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card data-testid="stat-rating"><CardContent className="p-5">
          <div className="text-xs font-mono uppercase text-slate-500">Rating</div>
          <div className="font-display font-extrabold text-3xl mt-2 flex items-center gap-1">{profile.avg_rating.toFixed(1)}<Star className="w-5 h-5 fill-amber-400 text-amber-400" /></div>
        </CardContent></Card>
        <Card data-testid="stat-jobs-done"><CardContent className="p-5">
          <div className="text-xs font-mono uppercase text-slate-500">Jobs Done</div>
          <div className="font-display font-extrabold text-3xl mt-2">{profile.jobs_completed}</div>
        </CardContent></Card>
        <Card data-testid="stat-zones"><CardContent className="p-5">
          <div className="text-xs font-mono uppercase text-slate-500">Zones</div>
          <div className="font-display font-extrabold text-3xl mt-2">{profile.postal_codes?.length || 0}</div>
        </CardContent></Card>
        <Card data-testid="stat-earnings"><CardContent className="p-5">
          <div className="text-xs font-mono uppercase text-slate-500">Earnings (80%)</div>
          <div className="font-display font-extrabold text-3xl mt-2 text-teal-600">${earnings ? earnings.total_earned.toFixed(0) : "0"}</div>
        </CardContent></Card>
      </div>

      {/* Quick nav cards to dedicated sub-pages */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="portal-quicknav">
        <button onClick={() => nav("/cleaner/available")} data-testid="quick-available" className="text-left">
          <Card className="border-teal-200 hover:shadow-md hover:border-teal-400 transition-all h-full">
            <CardContent className="p-5">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"><Zap className="w-5 h-5" /></div>
              <div className="mt-3 font-display font-bold text-slate-900">Available Jobs</div>
              <div className="text-xs text-slate-500 mt-1">{profile.verification_status === "approved" ? `${availableCount} in your zones` : "Unlocks once verified"}</div>
              <div className="mt-3 text-xs font-mono uppercase tracking-widest text-teal-600 inline-flex items-center gap-1">Open <ArrowRight className="w-3 h-3" /></div>
            </CardContent>
          </Card>
        </button>
        <button onClick={() => nav("/cleaner/schedule")} data-testid="quick-schedule" className="text-left">
          <Card className="border-slate-200 hover:shadow-md hover:border-slate-400 transition-all h-full">
            <CardContent className="p-5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><Calendar className="w-5 h-5" /></div>
              <div className="mt-3 font-display font-bold text-slate-900">Active / Schedule</div>
              <div className="text-xs text-slate-500 mt-1">{active.length} active · {mine.filter(j => j.status === "completed").length} completed</div>
              <div className="mt-3 text-xs font-mono uppercase tracking-widest text-teal-600 inline-flex items-center gap-1">Open <ArrowRight className="w-3 h-3" /></div>
            </CardContent>
          </Card>
        </button>
        <button onClick={() => nav("/cleaner/earnings")} data-testid="quick-earnings" className="text-left">
          <Card className="border-slate-200 hover:shadow-md hover:border-slate-400 transition-all h-full">
            <CardContent className="p-5">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
              <div className="mt-3 font-display font-bold text-slate-900">Earnings</div>
              <div className="text-xs text-slate-500 mt-1">
                {earnings ? `$${earnings.available_balance.toFixed(2)} withdrawable` : "—"}
              </div>
              <div className="mt-3 text-xs font-mono uppercase tracking-widest text-teal-600 inline-flex items-center gap-1">Open <ArrowRight className="w-3 h-3" /></div>
            </CardContent>
          </Card>
        </button>
        <button onClick={() => nav("/cleaner/safety-checklist")} data-testid="quick-safety" className="text-left">
          <Card className="border-slate-200 hover:shadow-md hover:border-slate-400 transition-all h-full">
            <CardContent className="p-5">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
              <div className="mt-3 font-display font-bold text-slate-900">Safety Checklist</div>
              <div className="text-xs text-slate-500 mt-1">{eligibility?.eligible_for_transport_jobs ? "All clear" : "Action required"}</div>
              <div className="mt-3 text-xs font-mono uppercase tracking-widest text-teal-600 inline-flex items-center gap-1">Open <ArrowRight className="w-3 h-3" /></div>
            </CardContent>
          </Card>
        </button>
      </div>

      {/* Next active job — quick access to schedule */}
      {nextActive && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg text-slate-900">Next active job</h2>
            <button onClick={() => nav("/cleaner/schedule")} className="text-xs font-mono uppercase tracking-widest text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="portal-view-schedule">
              View full schedule <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <Card className="border-slate-200" data-testid={`portal-next-job-${nextActive.id}`}>
            <CardContent className="p-6 grid md:grid-cols-4 gap-4 items-center">
              <div>
                <Badge className={`${STATUS_STYLES[nextActive.status]} border font-mono text-[10px] uppercase`}>{nextActive.status.replace(/_/g, " ")}</Badge>
                <div className="mt-2 font-display font-bold">{nextActive.bedrooms}b/{nextActive.bathrooms}ba{nextActive.deep_clean && " · Deep"}</div>
                <div className="text-xs text-slate-500 mt-1">
                  <MapPin className="w-3 h-3 inline" />{" "}
                  {nextActive.street_address ? `${nextActive.street_address}, ${[nextActive.city, nextActive.state, nextActive.postal_code].filter(Boolean).join(", ")}` : `${nextActive.postal_code}, ${nextActive.country}`}
                </div>
              </div>
              <div><div className="text-xs font-mono uppercase text-slate-500">Scheduled</div><div className="text-sm mt-1">{new Date(nextActive.scheduled_at).toLocaleString()}</div></div>
              <div><div className="text-xs font-mono uppercase text-slate-500">Your payout</div><div className="font-display font-bold text-xl text-teal-600 mt-1">${(nextActive.cleaner_payout / (nextActive.team_size || 1)).toFixed(2)}</div></div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => nav("/cleaner/schedule")} data-testid="portal-manage-next-job">
                  <ClipboardList className="w-4 h-4 mr-1" />Manage
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Agreement HARD-BLOCK dialog — unchanged. */}
      <Dialog open={requireAgreement} onOpenChange={() => { /* not dismissible */ }}>
        <DialogContent
          className="max-w-2xl"
          data-testid="agreement-enforce-dialog"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Review &amp; accept the CLEANBAZ Cleaner Agreement</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              <b>Required:</b> Before CLEANBAZ can dispatch or let you accept jobs, you must review and accept the current version of the Cleaner Agreement ({agreementDoc?.version || eligibility?.agreement_current_version}). If CLEANBAZ later publishes a new version, you'll be asked to re-accept before your next job.
            </div>
            <div className="text-xs text-teal-800 bg-teal-50 border border-teal-200 rounded p-3">
              This Agreement references the separate <a className="underline font-semibold" href="/legal/dispute-policy" target="_blank" rel="noopener noreferrer">CLEANBAZ Dispute &amp; Damage Policy</a>. CLEANBAZ does not automatically decide fault or promise reimbursement.
            </div>
            {!agreementDoc && <div className="text-slate-500">Loading current agreement…</div>}
            {agreementDoc?.sections?.map((s) => (
              <div key={s.key}>
                <div className="font-semibold text-slate-900">{s.title}</div>
                <div className="whitespace-pre-wrap text-slate-700">{s.body}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              onClick={acceptCurrentAgreement}
              disabled={agreementAccepting || !agreementDoc}
              data-testid="agreement-enforce-accept"
              className="bg-teal-600 hover:bg-teal-700 text-white w-full"
            >
              {agreementAccepting ? "Recording…" : "I have read and accept this Agreement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
